import { useState } from "react";
import { toast } from "sonner";
import {
  Video, Building2, Clock, CalendarDays, ArrowRight, Loader2, ShieldCheck,
  Star, Award, Users, Lock, ChevronRight, MapPin, Sparkles,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = process.env.REACT_APP_BACKEND_URL;
const WD = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];
const brl = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);
const fmtDay = (iso) => { const d = new Date(iso + "T00:00:00"); return `${WD[d.getDay()]} · ${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}`; };

export default function ClinicPublicView({ slug, clinic, specialties, doctors }) {
  const [spec, setSpec] = useState(specialties[0] || null);
  const [openDoc, setOpenDoc] = useState(null);
  const [modality, setModality] = useState(null);
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState({ patient_name: "", patient_email: "", patient_phone: "" });
  const [booking, setBooking] = useState(false);

  const list = doctors.filter((d) => (d.specialty || "").trim() === spec);
  const doc = doctors.find((d) => d.id === openDoc);
  const price = doc ? (modality === "online" ? doc.price_online : doc.price_presencial) : 0;
  const lowest = doctors.reduce((m, d) => {
    const vals = [d.price_presencial, d.price_online].filter((x) => x > 0);
    return vals.length ? Math.min(m, ...vals) : m;
  }, Infinity);
  const fromPrice = isFinite(lowest) ? lowest : 0;

  const selectDoc = (d) => { setOpenDoc(d.id); setSel(null); setModality(d.modality === "ambos" ? null : d.modality); };

  const submit = async () => {
    if (!modality) return toast.error("Escolha a modalidade.");
    if (!sel) return toast.error("Escolha um dia e horário.");
    if (!form.patient_name.trim() || !form.patient_email.trim()) return toast.error("Informe seu nome e e-mail.");
    setBooking(true);
    try {
      const r = await fetch(`${API}/api/public/booking`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ slug, doctor_id: doc.id, date: sel.date, time: sel.time, modality, ...form, origin_url: window.location.origin }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || "Erro ao reservar");
      toast.success("Horário reservado por 30 minutos!", { description: "Você será levado ao pagamento." });
      window.location.href = j.checkout_url;
    } catch (e) {
      toast.error("Não foi possível reservar", { description: e.message });
      setBooking(false);
    }
  };

  const initials = (clinic.name || "C").split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]).join("").toUpperCase();

  const Stat = ({ icon: Icon, value, label }) => (
    <div className="flex flex-col items-center rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-center">
      <Icon className="h-4 w-4 text-emerald-600" strokeWidth={2.2} />
      <span className="mt-1 font-heading text-base font-extrabold text-slate-900">{value}</span>
      <span className="text-[10px] font-medium uppercase tracking-wide text-slate-400">{label}</span>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 pb-16" data-testid="clinic-public-page">
      {/* top bar */}
      <div className="sticky top-0 z-40 border-b border-slate-200/80 bg-white/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-5 py-3">
          <span className="font-heading text-lg font-extrabold tracking-tight text-slate-900">Velo<span className="text-emerald-600">Clin</span></span>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700"><ShieldCheck className="h-3 w-3" /> Clínica verificada</span>
        </div>
      </div>

      <div className="mx-auto max-w-4xl px-5 pt-8">
        {/* header: logo (no cover), name, trust */}
        <div className="flex flex-col items-center text-center sm:flex-row sm:items-center sm:gap-5 sm:text-left">
          <div className="rounded-2xl bg-gradient-to-tr from-emerald-500 via-teal-500 to-cyan-500 p-[3px] shadow-lg">
            <div className="flex h-24 w-24 items-center justify-center overflow-hidden rounded-2xl border-4 border-white bg-white">
              {clinic.logo_url ? <img src={clinic.logo_url} alt={clinic.name} className="h-full w-full object-cover" /> : <span className="font-heading text-2xl font-extrabold text-emerald-600">{initials}</span>}
            </div>
          </div>
          <div className="mt-4 sm:mt-0">
            <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl" data-testid="clinic-name">{clinic.name}</h1>
            <div className="mt-2 flex flex-wrap items-center justify-center gap-1.5 sm:justify-start">
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-bold text-amber-700"><Star className="h-3 w-3 fill-amber-400 text-amber-400" /> 4,9</span>
              <span className="text-xs font-medium text-slate-500">· {doctors.length} especialista{doctors.length !== 1 ? "s" : ""} · {specialties.length} especialidade{specialties.length !== 1 ? "s" : ""}</span>
            </div>
          </div>
        </div>

        {/* hero copy */}
        <div className="mt-7">
          <h2 className="font-heading text-2xl font-bold leading-tight tracking-tight text-slate-900 sm:text-3xl">Cuide da sua saúde com uma equipe em quem você confia</h2>
          <p className="mt-2 max-w-xl text-base leading-relaxed text-slate-600">
            {clinic.bio ? clinic.bio + " " : ""}Escolha a especialidade, veja os horários disponíveis e agende em minutos — presencial ou por telemedicina, com toda a segurança da VeloClin.
          </p>
        </div>

        {/* stats */}
        <div className="mt-6 grid grid-cols-3 gap-3">
          <Stat icon={Users} value="+5.000" label="Pacientes atendidos" />
          <Stat icon={Award} value={`${doctors.length}+`} label="Especialistas" />
          <Stat icon={Star} value="4,9/5" label="Satisfação" />
        </div>

        {/* scarcity */}
        <div className="mt-5 flex items-center gap-3 rounded-xl border border-amber-200/80 bg-amber-50 p-3 text-sm font-medium text-amber-900">
          <Sparkles className="h-4 w-4 shrink-0 text-amber-600" />
          <span>Agendas concorridas — os melhores horários costumam esgotar rápido. Garanta o seu agora.</span>
        </div>

        {/* menu de especialidades */}
        <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="font-heading text-sm font-bold uppercase tracking-wide text-slate-400">1. Escolha a especialidade</h3>
          {specialties.length === 0 ? (
            <p className="mt-3 text-sm text-slate-400">Nenhuma especialidade disponível no momento.</p>
          ) : (
            <div className="mt-3 flex flex-wrap gap-2">
              {specialties.map((s) => (
                <button key={s} data-testid={`spec-${s}`} onClick={() => { setSpec(s); setOpenDoc(null); }} className={`rounded-full border px-4 py-2 text-sm font-semibold transition-all ${spec === s ? "border-emerald-600 bg-emerald-600 text-white shadow" : "border-slate-200 text-slate-700 hover:border-emerald-300 hover:bg-emerald-50"}`}>
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* médicos da especialidade */}
        {spec && (
          <div className="mt-5">
            <h3 className="mb-2 font-heading text-sm font-bold uppercase tracking-wide text-slate-400">2. Escolha o profissional</h3>
            <div className="space-y-3">
              {list.length === 0 && <p className="rounded-2xl border border-dashed border-slate-200 bg-white py-8 text-center text-sm text-slate-400">Nenhum profissional nesta especialidade.</p>}
              {list.map((d) => (
                <div key={d.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={`clinic-doctor-${d.id}`}>
                  <button onClick={() => selectDoc(d)} data-testid={`clinic-doctor-open-${d.id}`} className="flex w-full items-center gap-3 text-left">
                    <div className="rounded-full bg-gradient-to-tr from-emerald-500 via-teal-500 to-cyan-500 p-[2px]">
                      <div className="h-14 w-14 shrink-0 overflow-hidden rounded-full border-2 border-white bg-slate-100">
                        {d.photo_url ? <img src={d.photo_url} alt={d.name} className="h-full w-full object-cover" /> : <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-emerald-500 to-teal-600 text-lg font-bold text-white">{d.name.charAt(0)}</div>}
                      </div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-bold text-slate-900">{d.name}</p>
                      <p className="text-xs font-semibold text-emerald-600">{d.specialty}</p>
                      <p className="mt-0.5 flex flex-wrap gap-2 text-[11px] text-slate-400">
                        {d.modality !== "online" && <span>Presencial {brl(d.price_presencial)}</span>}
                        {d.modality !== "presencial" && <span>Online {brl(d.price_online)}</span>}
                      </p>
                    </div>
                    <ChevronRight className={`h-5 w-5 text-slate-300 transition-transform ${openDoc === d.id ? "rotate-90" : ""}`} />
                  </button>

                  {openDoc === d.id && (
                    <div className="mt-4 border-t border-slate-100 pt-4">
                      <div className="grid gap-2 sm:grid-cols-2">
                        {d.modality !== "online" && (
                          <button data-testid={`cmod-presencial-${d.id}`} onClick={() => setModality("presencial")} className={`flex items-center gap-2 rounded-xl border p-3 text-left transition-all ${modality === "presencial" ? "border-emerald-600 bg-emerald-50 ring-1 ring-emerald-600" : "border-slate-200 hover:bg-slate-50"}`}>
                            <Building2 className="h-4 w-4 text-emerald-600" /><span className="text-sm font-semibold text-slate-800">Presencial · {brl(d.price_presencial)}</span>
                          </button>
                        )}
                        {d.modality !== "presencial" && (
                          <button data-testid={`cmod-online-${d.id}`} onClick={() => setModality("online")} className={`flex items-center gap-2 rounded-xl border p-3 text-left transition-all ${modality === "online" ? "border-emerald-600 bg-emerald-50 ring-1 ring-emerald-600" : "border-slate-200 hover:bg-slate-50"}`}>
                            <Video className="h-4 w-4 text-teal-600" /><span className="text-sm font-semibold text-slate-800">Online · {brl(d.price_online)}</span>
                          </button>
                        )}
                      </div>
                      {modality === "presencial" && d.address && <p className="mt-2 flex items-center gap-1.5 text-xs text-slate-500"><MapPin className="h-3.5 w-3.5" /> {d.address}</p>}
                      <div className="mt-4">
                        <h4 className="flex items-center gap-2 text-sm font-bold text-slate-900"><CalendarDays className="h-4 w-4 text-emerald-600" /> Horários disponíveis</h4>
                        {d.days.length === 0 ? <p className="mt-2 text-sm text-slate-400">Sem horários disponíveis.</p> : (
                          <div className="mt-2 max-h-64 space-y-3 overflow-y-auto pr-1">
                            {d.days.map((day) => (
                              <div key={day.date}>
                                <p className="mb-1.5 text-xs font-bold text-slate-600">{fmtDay(day.date)} {day.slots.length <= 2 && <span className="ml-1 rounded bg-rose-50 px-1.5 py-0.5 text-[10px] font-bold text-rose-600">última{day.slots.length > 1 ? "s" : ""} vaga{day.slots.length > 1 ? "s" : ""}</span>}</p>
                                <div className="flex flex-wrap gap-1.5">
                                  {day.slots.map((t) => {
                                    const active = sel?.date === day.date && sel?.time === t;
                                    return <button key={t} data-testid={`cslot-${d.id}-${day.date}-${t}`} onClick={() => setSel({ date: day.date, time: t })} className={`min-w-[52px] rounded-lg border px-2.5 py-1.5 text-sm font-semibold transition-all ${active ? "border-emerald-600 bg-emerald-600 text-white shadow" : "border-slate-200 text-slate-700 hover:border-emerald-300 hover:bg-emerald-50"}`}>{t}</button>;
                                  })}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      {sel && modality && (
                        <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50/40 p-4" data-testid="clinic-booking-form">
                          <div className="mb-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2.5">
                            <Clock className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                            <p className="text-xs text-amber-800">Você tem <b>30 minutos</b> para concluir o pagamento e garantir o horário.</p>
                          </div>
                          <p className="text-sm text-slate-600">{d.name} — <b>{modality}</b> · {fmtDay(sel.date)} às <b>{sel.time}</b> · <span className="font-mono-num font-bold text-slate-900">{brl(price)}</span></p>
                          <div className="mt-3 grid gap-2 sm:grid-cols-3">
                            <div><Label className="text-xs font-semibold text-slate-600">Nome</Label><Input data-testid="cpatient-name" placeholder="Seu nome" value={form.patient_name} onChange={(e) => setForm({ ...form, patient_name: e.target.value })} className="mt-1" /></div>
                            <div><Label className="text-xs font-semibold text-slate-600">E-mail</Label><Input data-testid="cpatient-email" type="email" placeholder="voce@email.com" value={form.patient_email} onChange={(e) => setForm({ ...form, patient_email: e.target.value })} className="mt-1" /></div>
                            <div><Label className="text-xs font-semibold text-slate-600">Telefone</Label><Input data-testid="cpatient-phone" placeholder="(11) 90000-0000" value={form.patient_phone} onChange={(e) => setForm({ ...form, patient_phone: e.target.value })} className="mt-1" /></div>
                          </div>
                          <button data-testid="cpay-btn" onClick={submit} disabled={booking} className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 py-3.5 text-sm font-bold text-white shadow-md shadow-emerald-600/20 transition-all hover:bg-emerald-700 disabled:opacity-60">
                            {booking ? <><Loader2 className="h-4 w-4 animate-spin" /> Reservando…</> : <>Agendar e pagar · {brl(price)} <ArrowRight className="h-4 w-4" /></>}
                          </button>
                          <p className="mt-2 flex items-center justify-center gap-1.5 text-[11px] text-slate-400"><Lock className="h-3 w-3" /> Pagamento 100% seguro via Stripe</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        <p className="mt-8 text-center text-xs text-slate-400">Powered by VeloClin · Pagamentos criptografados</p>
      </div>
    </div>
  );
}
